import { TraceXInterceptor } from '@trace-x/trace-x-generator';
import { Injectable } from '@angular/core';
import {
  GENERAL_SEPARATORS,
  OntologyResponse,
  SPLITTER,
} from '@surf/surf-components-core';
import { BehaviorSubject } from 'rxjs';
import { TariffHotelCriteria } from '../models/tariff-accom-criteria';
import { map } from 'rxjs/operators';

export type Type =
  | 'hotel'
  | 'country'
  | 'city';

export type TypeName =
  | 'hotelName'
  | 'countryName'
  | 'cityName';

export type CriteriaField = Pick<
  TariffHotelCriteria,
  | 'hotelName'
  | 'hotelCountry'
  | 'hotelCity'
>;

interface TypeAheadStore {
  type: Type;
  selected: Partial<OntologyResponse>;
  placeholderText: string;
  searchInitialized: boolean;
  ontologies: OntologyResponse[];
  filteredList: any[];
  URL: string;
  names: string;
  multiSelect: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TypeAheadService {
  private initialStore: Partial<TypeAheadStore>[] = [
    {
      type: 'hotel',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select hotel',
    },
    {
      type: 'country',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select country',
    },
    {
      type: 'city',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select city',
    },
  ];

  constructor() {
    this.initializeTypes(this.initialStore);
  }

  // tslint:disable-next-line:variable-name
  private readonly _typeAheadStore = new BehaviorSubject([]);

  readonly typeAheadStore$ = this._typeAheadStore.asObservable();

  get typeAheadStore() {
    return this._typeAheadStore.getValue();
  }

  set typeAheadStore(val) {
    this._typeAheadStore.next(val);
  }

  private initializeType(typeAhead: Partial<TypeAheadStore>, options?: Partial<Record<keyof TypeAheadStore, boolean>>) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.initializeType');
    const store = this.typeAheadStore;
    const index = store.findIndex((item) => item.type === typeAhead.type);

    const defaultValues: Partial<TypeAheadStore> = {
      ontologies: [],
      searchInitialized: false,
      filteredList: [],
      URL: '',
      names: '',
      multiSelect: false,
      selected: new OntologyResponse(),
    };

    if (index > -1) {
      defaultValues.URL =  options?.URL ? store[index]?.URL : defaultValues.URL;

      store[index] = { ...store[index], ...typeAhead, ...defaultValues };
      this.typeAheadStore = store;
    } else {
      this.typeAheadStore = [...store, { ...typeAhead, ...defaultValues }];
    }
  }

  private initializeTypes(store: Partial<TypeAheadStore>[], options?: Partial<Record<keyof TypeAheadStore, boolean>>) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.initializeTypes');
    store.forEach((item) => {
      this.initializeType(item, options);
    });
  }

  public updateTypeAheadStore(
    typeToUpdate: Type,
    value: Partial<TypeAheadStore>
  ) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.updateTypeAheadStore');
    const store = this.typeAheadStore;
    const index = store.findIndex((item) => item.type === typeToUpdate);
    if (index > -1) {
      store[index] = {
        ...store[index],
        ...value,
        ontologies: [...store[index].ontologies, ...(value?.ontologies || [])],
      };
      this.typeAheadStore = store;
    }
  }

  public updateOntologiesByType(typeToUpdate: Type, ontologies: any[]) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.updateOntologiesByType');
    const store = this.typeAheadStore;
    const index = store.findIndex((item) => item.type === typeToUpdate);
    if (index > -1) {
      store[index] = {
        ...store[index],
        ontologies: [...store[index].ontologies, ...ontologies],
      };
      this.typeAheadStore = store;
    }
  }

  public removeOntologyByType(typeToUpdate: Type, event) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.removeOntologyByType');
    const store = this.typeAheadStore;
    const index = store.findIndex((item) => item.type === typeToUpdate);
    if (index > -1) {
      const indexOfOntologyToBeRemoved = store[index].ontologies?.findIndex(
        (ontology) => ontology.displayText === event.value
      );
      if (indexOfOntologyToBeRemoved > -1) {
        store[index]?.ontologies?.splice(indexOfOntologyToBeRemoved, 1);
        this.typeAheadStore = store;
      }

    }
  }

  // reset type ahead item to initial state
  public resetTypeAheadItem(typeToBeReset: Type) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.resetTypeAheadItem');
    const store = this.typeAheadStore;
    const index = store.findIndex((item) => item.type === typeToBeReset);
    if (index > -1) {
      store[index] = {
        ...store[index],
        ontologies: [],
        searchInitialized: false,
        filteredList: [],
        names: '',
        selected: new OntologyResponse(),
      };
      this.typeAheadStore = store;
    }
  }

  public resetTypeAheadStore(optionsToKeep?: Partial<Record<keyof TypeAheadStore, boolean>>) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.resetTypeAheadStore');
    this.initializeTypes(this.initialStore, optionsToKeep);
  }

  public getTypeAheadByType(typeToBeFound: Type): TypeAheadStore {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.getTypeAheadByType');
    return this.typeAheadStore.find((item) => item.type === typeToBeFound);
  }

  public returnedValues(
    data: any[],
    typeToBeUpdated: Type,
    criteria: TariffHotelCriteria,
  ) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.returnedValues');
    let filteredList = [];
    const criteriaField = this.getHotelCriteriaFieldMappedToType(typeToBeUpdated);

    if (data && data.length > 0) {
      filteredList = data;
    } else {
      const ontologies = this.getTypeAheadByType(typeToBeUpdated).ontologies;
      if (criteria && ontologies && ontologies.length === 0) {
        criteria[criteriaField] = null;
      }
    }

    this.updateTypeAheadStore(typeToBeUpdated, {
      filteredList,
    });
  }

  selectType(
    data,
    typeToBeSelected: Type,
    criteria: TariffHotelCriteria,
    typeName?: TypeName
  ) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.selectType');
    const type = this.getTypeAheadByType(typeToBeSelected);
    const criteriaField = this.getHotelCriteriaFieldMappedToType(typeToBeSelected);

    if (
      (type.ontologies &&
      type.ontologies.filter(
        (ontology) =>
          ontology.displayText === this.getOntologyDisplayText(data, false)
      ).length === 0) && type.multiSelect
    ) {
      type.ontologies.push(
        this.createOntologyData(data, type.ontologies.length, false)
      );
    } else {
      type.ontologies = [];
      type.ontologies.push(
        this.createOntologyData(data, type.ontologies.length, false)
      );
    }

    const typeCopy = { ...type };
    typeCopy.filteredList = [];
    typeCopy.ontologies = [];

    if (criteria && type.ontologies && type.ontologies.length > 0) {
      criteria[criteriaField] = this.createMultiSelectCriteriaString(
        type.ontologies,
        typeCopy.names,
        false
      );
      criteria[typeName] = data.name; // to be used to get display text
    }

    typeCopy.selected.displayText = data.name;
    typeCopy.searchInitialized = false;

    this.updateTypeAheadStore(typeToBeSelected, typeCopy);
  }

  public getOntologyDisplayText(data, isWithCode = false) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.getOntologyDisplayText');
    return isWithCode ? data.name + ' (' + data.code + ')' : data.name;
  }

  public createOntologyData(
    data: any,
    index: number,
    isWithCode = false
  ): OntologyResponse {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.createOntologyData');
    const res = new OntologyResponse();
    if (data) {
      res.displayText = this.getOntologyDisplayText(data, isWithCode);
      res.query = data.code;
      res.index = index;
    }
    return res;
  }

  public createMultiSelectCriteriaString(
    ontologies,
    names,
    isCommaSeparated = true
  ) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.createMultiSelectCriteriaString');
    let separator = SPLITTER.COMMA_SPLITTER;
    if (!isCommaSeparated) {
      separator = GENERAL_SEPARATORS.FIELD_SEPARATOR;
    }
    if (ontologies.length > 0) {
      let returningText = '';
      names = '';
      ontologies.forEach((ontology) => {
        if (returningText.length > 0) {
          returningText += separator;
          names += separator;
        }
        if (ontology && ontology.query) {
          returningText += ontology.query;
          names += ontology.displayText;
        } else {
          console.error('Error in setting data codes');
        }
      });
      return returningText;
    }
    return null;
  }

  public getHotelCriteriaFieldMappedToType(type: Type): keyof CriteriaField {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/services/type-ahead.service.ts.TypeAheadService.getHotelCriteriaFieldMappedToType');
    let criteriaField: keyof CriteriaField;

    if (type === 'hotel') {
      criteriaField = 'hotelName';
    } else if (type === 'country') {
      criteriaField = 'hotelCountry';
    } else if (type === 'city') {
      criteriaField = 'hotelCity';
    } else {
      throw new Error('Invalid type passed to function');
    }

    return criteriaField;
  }
}
